import { useCallback, useState } from "react";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { format } from "date-fns";
import { getDateFnsLocale } from "../../../utils/i18nLanguage";
import { logoPlatform } from "../../../utils/string";
import { APP_DROPDOWN_Z_INDEX_CSS } from "../../../utils/modalLayer";
import {
  buildFailureRowsFromScheduledContent,
  getOrganicPlatformLabel,
  getRetryablePlatformsFromScheduledEvent,
  isScheduledEventRetryable,
} from "../../../utils/organicPublishOutcome";
import { runOrganicPublishRetryFlow } from "../../../utils/runOrganicPublishRetryFlow";
import { confirmOrganicPublishRetry } from "../../Alerts/confirmOrganicPublishRetry";
import { getCalendarEventDisplayStatus } from "../../../utils/calendarEventPublishStatus";
import {
  INSTAGRAM,
  TIKTOK,
  YOUTUBE,
  BORDER_RADIUS_BUTTON,
  FONT_SIZE_BUTTON,
  ADS_CAMPAIGN_MEDIA_FROM_CREATIFY,
  BORDER_RADIUS,
  STATES,
  FACEBOOK,
  LINKEDIN,
} from "../../../utils/constants";
import { BOX_SHADOW_BUTTON, INPUT_COLOR_BORDER } from "../../../utils/colors";
import { useTranslationApp } from "../../../lib/i18next";
import CardMedia from "../../Media/CardMedia";
import Button from "../../Buttons/Button";
import { useDispatchApp } from "../../../lib/redux";
import { startDeleteScheduledContent } from "../../../actions/user";
import { collectScheduledContentIDs } from "../../../utils/galleryPublishedSync";
import { ConfirmAlert, SimpleAlert } from "../../Alerts/Alerts";
import {
  ALERT_SHARE_SUCCESS,
  BUTTON_DELETE_CONTENT,
  BUTTON_RETRY_PUBLISHING,
  DELETE_SCHEDULED_CONTENT_CONFIRMATION_DESCRIPTION,
  DELETE_SCHEDULED_CONTENT_CONFIRMATION_TITLE,
  DELETE_SCHEDULED_CONTENT_ERROR_DESCRIPTION,
  DELETE_SCHEDULED_CONTENT_SUCCESS,
  ERROR,
  ORGANIC_PUBLISH_RETRY_ERROR,
  ORGANIC_PUBLISH_PARTIAL_STATUS,
  READY,
  EVENT_DETAILS_DESCRIPTION,
  EVENT_DETAILS_DATE_AND_TIME,
  EVENT_DETAILS_SOCIAL_NETWORK,
  EVENT_DETAILS_STATUS,
  EVENT_DETAILS_STATUS_SCHEDULED,
  EVENT_DETAILS_STATUS_PUBLISHED,
  EVENT_DETAILS_STATUS_FAILED,
  EVENT_DETAILS_NETWORK_INSTAGRAM,
  EVENT_DETAILS_NETWORK_TIKTOK,
  EVENT_DETAILS_NETWORK_YOUTUBE,
  EVENT_DETAILS_NETWORK_FACEBOOK,
  EVENT_DETAILS_NETWORK_LINKEDIN,
  EVENT_DETAILS_CONTENT,
  EVENT_DETAILS_AUTOMATION,
  CALENDAR_SOURCE_AUTOMATED,
} from "../../../i18n/keysTranslations";

import { LaunchError } from "../../../utils/errors";

const STATUS_META = {
  [STATES.SCHEDULED]: {
    icon: <AccessTimeIcon sx={{ color: "text.secondary", fontSize: "18px" }} />,
  },
  [STATES.PUBLISHED]: {
    icon: <CheckCircleIcon sx={{ color: "#10b981", fontSize: "18px" }} />,
  },
  [STATES.FAILED]: {
    icon: <ErrorOutlineIcon sx={{ color: "#ef4444", fontSize: "18px" }} />,
  },
};

// Component: Close button
const CloseButton = ({ onClose }) => {
  return (
    <IconButton
      onClick={onClose}
      sx={{
        position: "absolute",
        top: 9,
        right: 8,
        padding: 0.5,
        color: "text.secondary",
        "&:hover": {
          color: "text.primary",
          backgroundColor: "#f2f2f2",
        },
      }}
      size="small"
    >
      <CloseIcon fontSize="medium" />
    </IconButton>
  );
};

// Component: Event title
const EventTitle = ({ title, isMobile = false }) => {
  return (
    <Typography
      variant="h6"
      sx={{
        fontWeight: 600,
        mb: 1.5,
        fontSize: "1.1rem",
        lineHeight: 1.3,
        pr: isMobile ? 0 : 4,
      }}
    >
      {title}
    </Typography>
  );
};

// Component: Event description
const EventDescription = ({ description }) => {
  const { t } = useTranslationApp();

  if (!description) return null;

  return (
    <>
      <Divider sx={{ mb: 1.5 }} />
      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 0.5,
            fontSize: "0.75rem",
          }}
        >
          {t(EVENT_DETAILS_DESCRIPTION)}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.primary" }}>
          {description}
        </Typography>
      </Box>
    </>
  );
};

// Component: Event date and time
const EventDateAndTime = ({ startDate, locale, timezone = "" }) => {
  const { t } = useTranslationApp();

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 0.5, fontSize: "0.75rem" }}
      >
        {t(EVENT_DETAILS_DATE_AND_TIME)}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {format(startDate, "MMMM d, yyyy", {
          locale: locale,
        }).replace(/^\w/, (c) => c.toUpperCase())}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {format(startDate, "h:mm a", { locale: locale })}
        {timezone ? ` · ${timezone}` : ""}
      </Typography>
    </Box>
  );
};

const PLATFORM_CONFIG = {
  [INSTAGRAM]: {
    constant: INSTAGRAM,
    labelKey: EVENT_DETAILS_NETWORK_INSTAGRAM,
  },
  [FACEBOOK]: {
    constant: FACEBOOK,
    labelKey: EVENT_DETAILS_NETWORK_FACEBOOK,
  },
  [LINKEDIN]: {
    constant: LINKEDIN,
    labelKey: EVENT_DETAILS_NETWORK_LINKEDIN,
  },
  [TIKTOK]: {
    constant: TIKTOK,
    labelKey: EVENT_DETAILS_NETWORK_TIKTOK,
  },
  [YOUTUBE]: {
    constant: YOUTUBE,
    labelKey: EVENT_DETAILS_NETWORK_YOUTUBE,
  },
};

const toTitleCase = (text = "") =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const getPlatformConfig = (platform, t) => {
  const config = PLATFORM_CONFIG[platform];

  const constant = config?.constant ?? INSTAGRAM;
  const label = config?.labelKey ? t(config.labelKey) : toTitleCase(platform);

  return { constant, label };
};

// Component: Event social network
const EventNetwork = ({ platforms = [], chipStyles, channelStates = {} }) => {
  const { t } = useTranslationApp();

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 0.5, fontSize: "0.75rem" }}
      >
        {t(EVENT_DETAILS_SOCIAL_NETWORK)}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          alignContent: "center",
        }}
      >
        {platforms.map((platform) => {
          const { constant, label } = getPlatformConfig(platform, t);
          const logo = logoPlatform(constant);
          const isFailedChannel = channelStates?.[platform] === STATES.FAILED;

          return (
            <Box
              key={platform}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.75,
                height: 32,
              }}
            >
              <Chip
                avatar={
                  <Avatar
                    src={logo}
                    variant="square"
                    sx={{
                      width: "20px",
                      height: "20px",
                      ".MuiAvatar-img": { objectFit: "contain" },
                    }}
                  />
                }
                label={label}
                size="small"
                sx={{
                  ...chipStyles,
                  height: 32,
                  alignSelf: "center",
                  "& .MuiChip-avatar": { marginLeft: 1 },
                }}
              />
              {isFailedChannel && (
                <Box
                  component="svg"
                  viewBox="0 0 22 22"
                  width={15}
                  height={15}
                  sx={{ flexShrink: 0, alignSelf: "center", display: "block" }}
                  aria-hidden
                >
                  <circle cx={11} cy={11} r={11} fill="#ef4444" />
                  <text
                    x={11}
                    y={11}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#ffffff"
                    fontSize={12}
                    fontWeight={700}
                    fontFamily={
                      'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
                    }
                  >
                    !
                  </text>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const getEventStatus = (statesByChannel) => {
  const { state, isPartialFailure } =
    getCalendarEventDisplayStatus(statesByChannel);

  if (isPartialFailure) {
    return {
      state: STATES.FAILED,
      icon: <ErrorOutlineIcon sx={{ color: "#f59e0b", fontSize: "18px" }} />,
      isPartialFailure: true,
    };
  }

  return {
    state,
    icon: STATUS_META[state]?.icon ?? STATUS_META[STATES.SCHEDULED].icon,
    isPartialFailure: false,
  };
};

// Component: Event status
const EventStatus = ({
  states,
  chipStyles,
  showRetryPublishing = false,
  onRetryPublishing,
  isRetryPublishing = false,
}) => {
  const { t } = useTranslationApp();
  const { state, icon, isPartialFailure } = getEventStatus(states);

  const statusLabel = {
    [STATES.SCHEDULED]: t(EVENT_DETAILS_STATUS_SCHEDULED),
    [STATES.PUBLISHED]: t(EVENT_DETAILS_STATUS_PUBLISHED),
    [STATES.FAILED]: isPartialFailure
      ? t(ORGANIC_PUBLISH_PARTIAL_STATUS)
      : t(EVENT_DETAILS_STATUS_FAILED),
  };

  const canRetryPublishing =
    showRetryPublishing && typeof onRetryPublishing === "function";

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 0.5, fontSize: "0.75rem" }}
      >
        {t(EVENT_DETAILS_STATUS)}
      </Typography>

      <Chip
        icon={icon}
        label={statusLabel[state] || state}
        size="small"
        sx={{
          ...chipStyles,
          height: 32,
          "& .MuiChip-icon": { marginLeft: 1 },
        }}
      />

      {canRetryPublishing && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Button
            color="primary"
            fullWidth
            loading={isRetryPublishing}
            ariaLabel={t(BUTTON_RETRY_PUBLISHING)}
            onClick={onRetryPublishing}
            sx={{
              textTransform: "none",
              height: 40,
              minHeight: 40,
            }}
          >
            {t(BUTTON_RETRY_PUBLISHING)}
          </Button>
        </>
      )}
    </Box>
  );
};

const EVENT_PREVIEW_HEIGHT_DESKTOP = 200;
const EVENT_PREVIEW_HEIGHT_MOBILE = 300;

// Component: Individual media item
const MediaItem = ({ mediaUrl, mediaData, index, isMobile = false }) => {
  const url = typeof mediaUrl === "string" ? mediaUrl : mediaUrl?.url;

  if (!url) return null;

  const mediaHeight = isMobile
    ? EVENT_PREVIEW_HEIGHT_MOBILE
    : EVENT_PREVIEW_HEIGHT_DESKTOP;

  const mediaSx = {
    display: "block !important",
    width: "auto !important",
    height: "auto !important",
    maxWidth: "100% !important",
    maxHeight: `${mediaHeight}px !important`,
    objectFit: "contain !important",
    objectPosition: "left center",
    borderRadius: "8px",
    "&.MuiCardMedia-root, &.MuiCardMedia-img, &.MuiCardMedia-media": {
      width: "auto !important",
      height: "auto !important",
      maxWidth: "100% !important",
      maxHeight: `${mediaHeight}px !important`,
      objectFit: "contain !important",
      objectPosition: "left center",
    },
  };

  return (
    <Box
      key={index}
      sx={{
        display: "inline-block",
        alignSelf: "flex-start",
        width: "fit-content",
        maxWidth: "100%",
        maxHeight: mediaHeight,
        lineHeight: 0,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {mediaData?.from === ADS_CAMPAIGN_MEDIA_FROM_CREATIFY ? (
        <iframe
          src={mediaData?.preview}
          style={{
            display: "block",
            border: "none",
            overflow: "hidden",
            height: mediaHeight,
            width: "auto",
            maxWidth: "100%",
            maxHeight: mediaHeight,
          }}
        />
      ) : (
        <CardMedia
          url={url}
          eager
          propsType={{
            controls: false,
            autoplay: true,
            muted: true,
            loop: true,
          }}
          sx={mediaSx}
          sxImage={mediaSx}
          sxVideo={mediaSx}
        />
      )}
    </Box>
  );
};

// Component: Event content (media)
const EventContent = ({ urls, originalData, isMobile = false }) => {
  const { t } = useTranslationApp();
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!urls || urls.length === 0) return null;

  const hasMultipleMedia = urls.length > 1;

  const onPrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : urls.length - 1));
  };

  const onNext = () => {
    setCurrentIndex((prev) => (prev < urls.length - 1 ? prev + 1 : 0));
  };

  const currentMediaUrl = urls[currentIndex];
  const currentMediaData =
    typeof currentMediaUrl === "object" && currentMediaUrl?.mediaData
      ? currentMediaUrl.mediaData
      : originalData?.mediaData?.[currentIndex] || {};

  return (
    <>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 1, fontSize: "0.75rem" }}
      >
        {t(EVENT_DETAILS_CONTENT)}
      </Typography>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          width: "100%",
          gap: 1,
        }}
      >
        <MediaItem
          mediaUrl={currentMediaUrl}
          mediaData={currentMediaData}
          index={currentIndex}
          isMobile={isMobile}
        />

        {/* Navigation controls */}
        {hasMultipleMedia && (
          <>
            <IconButton
              onClick={onPrev}
              disabled={currentIndex === 0}
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.9)",
                },
                "&.Mui-disabled": {
                  opacity: 0.8,
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  cursor: "not-allowed",
                },
                zIndex: 1,
              }}
              size="small"
            >
              <ChevronLeftIcon sx={{ color: "#fff !important" }} />
            </IconButton>
            <IconButton
              onClick={onNext}
              disabled={currentIndex === urls.length - 1}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.9)",
                },
                "&.Mui-disabled": {
                  opacity: 0.8,
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  cursor: "not-allowed",
                },
                zIndex: 1,
              }}
              size="small"
            >
              <ChevronRightIcon sx={{ color: "#fff !important" }} />
            </IconButton>

            {/* Position indicator */}
            <Box
              sx={{
                position: "absolute",
                bottom: 12,
                left: "50%",
                transform: "translateX(-50%)",
                bgcolor: "rgba(0, 0, 0, 0.7)",
                px: 2,
                py: 0.5,
                borderRadius: 2,
                fontSize: "14px",
                fontWeight: 500,
                color: "#fff !important",
                zIndex: 1,
              }}
            >
              {currentIndex + 1} / {urls.length}
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

const EventAutomationMeta = ({ selectedEvent }) => {
  const { t } = useTranslationApp();
  const isAutomated = selectedEvent.source === "automation";
  const automationName = selectedEvent.automationName || "";

  if (!isAutomated) return null;

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 0.5, fontSize: "0.75rem" }}
      >
        {t(EVENT_DETAILS_AUTOMATION)}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {automationName || t(CALENDAR_SOURCE_AUTOMATED)}
      </Typography>
    </Box>
  );
};

// Component: Information column (left)
const EventInfoColumn = ({
  selectedEvent,
  locale,
  chipStyles,
  showRetryPublishing,
  onRetryPublishing,
  isRetryPublishing,
}) => {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <EventDateAndTime
        startDate={selectedEvent.start}
        locale={locale}
        timezone={
          selectedEvent.timezone || selectedEvent.originalData?.timezone
        }
      />
      <EventNetwork
        platforms={selectedEvent.platforms}
        chipStyles={chipStyles}
        channelStates={selectedEvent.states || {}}
      />
      <EventStatus
        states={selectedEvent.states}
        chipStyles={chipStyles}
        showRetryPublishing={showRetryPublishing}
        onRetryPublishing={onRetryPublishing}
        isRetryPublishing={isRetryPublishing}
      />
      <EventAutomationMeta selectedEvent={selectedEvent} />
    </Box>
  );
};

// Component: Content column (right)
const EventContentColumn = ({ selectedEvent }) => {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <EventContent
        urls={selectedEvent.originalData?.urls}
        originalData={selectedEvent.originalData}
      />
    </Box>
  );
};

// Component: Mobile layout - Network and Status in same row
const MobileNetworkAndStatus = ({
  selectedEvent,
  chipStyles,
  showRetryPublishing,
  onRetryPublishing,
  isRetryPublishing,
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <EventNetwork
        platforms={selectedEvent.platforms}
        chipStyles={chipStyles}
        channelStates={selectedEvent.states || {}}
      />
      <EventStatus
        states={selectedEvent.states}
        chipStyles={chipStyles}
        showRetryPublishing={showRetryPublishing}
        onRetryPublishing={onRetryPublishing}
        isRetryPublishing={isRetryPublishing}
      />
      <EventAutomationMeta selectedEvent={selectedEvent} />
    </Box>
  );
};

// Component: Mobile layout content
const MobileEventContent = ({ selectedEvent }) => {
  const { t } = useTranslationApp();
  const [currentIndex, setCurrentIndex] = useState(0);
  const urls = selectedEvent.originalData?.urls;

  if (!urls || urls.length === 0) return null;

  const hasMultipleMedia = urls.length > 1;

  const onPrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : urls.length - 1));
  };

  const onNext = () => {
    setCurrentIndex((prev) => (prev < urls.length - 1 ? prev + 1 : 0));
  };

  const currentMediaUrl = urls[currentIndex];
  const currentMediaData =
    typeof currentMediaUrl === "object" && currentMediaUrl?.mediaData
      ? currentMediaUrl.mediaData
      : selectedEvent.originalData?.mediaData?.[currentIndex] || {};

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 1, fontSize: "0.75rem" }}
      >
        {t(EVENT_DETAILS_CONTENT)}
      </Typography>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          width: "100%",
          gap: 1,
        }}
      >
        <MediaItem
          mediaUrl={currentMediaUrl}
          mediaData={currentMediaData}
          index={currentIndex}
          isMobile={true}
        />

        {/* Navigation controls */}
        {hasMultipleMedia && (
          <>
            <IconButton
              onClick={onPrev}
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.9)",
                },
                zIndex: 1,
              }}
              size="small"
            >
              <ChevronLeftIcon sx={{ color: "#fff !important" }} />
            </IconButton>
            <IconButton
              onClick={onNext}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.9)",
                },
                zIndex: 1,
              }}
              size="small"
            >
              <ChevronRightIcon sx={{ color: "#fff !important" }} />
            </IconButton>

            {/* Position indicator */}
            <Box
              sx={{
                position: "absolute",
                bottom: 12,
                left: "50%",
                transform: "translateX(-50%)",
                bgcolor: "rgba(0, 0, 0, 0.7)",
                px: 2,
                py: 0.5,
                borderRadius: 2,
                fontSize: "14px",
                fontWeight: 500,
                color: "#fff !important",
                zIndex: 1,
              }}
            >
              {currentIndex + 1} / {urls.length}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

// Component: Action buttons (Delete and Edit)
const ActionButtons = ({
  selectedEvent,
  onClose,
  onEdit,
  onRefreshCalendar,
  onPublicationMarksChanged,
  isMobile = false,
}) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [isDeleting, setIsDeleting] = useState(false);
  const isDemoContent = selectedEvent?.originalData?.isDemoContent === true;

  const onDeleteClick = async () => {
    if (!selectedEvent) return;

    const result = await ConfirmAlert({
      title: t(DELETE_SCHEDULED_CONTENT_CONFIRMATION_TITLE),
      text: t(DELETE_SCHEDULED_CONTENT_CONFIRMATION_DESCRIPTION),
      confirmButtonText: t(BUTTON_DELETE_CONTENT),
    });

    if (!result?.isConfirmed) return;

    const scheduleID = selectedEvent.id;
    const originalData = selectedEvent.originalData || {};

    // Para el nuevo formato, usar cloudTaskID, para el antiguo usar id
    const id = originalData.cloudTaskID || originalData.id;
    const queue = originalData.queue;

    if (!id || !scheduleID) {
      console.error("Missing required data to delete:", { id, scheduleID });
      SimpleAlert({
        icon: "error",
        title: t(ERROR),
        text: t(DELETE_SCHEDULED_CONTENT_ERROR_DESCRIPTION),
      });
      return;
    }

    setIsDeleting(true);

    try {
      const resultDelete = await dispatch(
        startDeleteScheduledContent({
          id: id,
          queue: queue || "",
          scheduleID: scheduleID,
        })
      );

      if (resultDelete?.ok) {
        onClose();
        SimpleAlert({
          icon: "success",
          title: t(READY),
          text: t(DELETE_SCHEDULED_CONTENT_SUCCESS),
        });
        if (onRefreshCalendar) {
          onRefreshCalendar();
        }
        onPublicationMarksChanged?.({
          contentIDs: collectScheduledContentIDs(originalData),
        });
      } else {
        SimpleAlert({
          icon: "error",
          title: t(ERROR),
          text: t(DELETE_SCHEDULED_CONTENT_ERROR_DESCRIPTION),
        });
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onDeleteClick",
          capture: "exception",
        })
      );
      console.error("Error deleting scheduled content:", error);
      SimpleAlert({
        icon: "error",
        title: t(ERROR),
        text: t(DELETE_SCHEDULED_CONTENT_ERROR_DESCRIPTION),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const onEditClick = () => {
    if (onEdit) {
      onEdit(selectedEvent);
    }
  };

  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <IconButton
          onClick={onEditClick}
          sx={{
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "#f2f2f2",
            },
          }}
          size="small"
        >
          <EditIcon fontSize="medium" />
        </IconButton>
        {!isDemoContent && (
          <IconButton
            onClick={onDeleteClick}
            disabled={isDeleting}
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "#f2f2f2",
              },
            }}
            size="small"
          >
            {isDeleting ? (
              <CircularProgress
                size={18}
                thickness={5}
                sx={{ color: "black" }}
              />
            ) : (
              <DeleteForeverIcon fontSize="medium" />
            )}
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "absolute",
        top: 8,
        right: 60,
        gap: 1,
        display: "flex",
        alignItems: "center",
      }}
    >
      <IconButton
        onClick={onEditClick}
        sx={{
          color: "text.secondary",
          "&:hover": {
            backgroundColor: "#f2f2f2",
          },
        }}
        size="small"
      >
        <EditIcon fontSize="medium" />
      </IconButton>
      {!isDemoContent && (
        <IconButton
          onClick={onDeleteClick}
          disabled={isDeleting}
          sx={{
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "#f2f2f2",
            },
          }}
          size="small"
        >
          {isDeleting ? (
            <CircularProgress size={18} thickness={5} sx={{ color: "black" }} />
          ) : (
            <DeleteForeverIcon fontSize="medium" />
          )}
        </IconButton>
      )}
    </Box>
  );
};

// Main component
const EventDetailsPopover = ({
  open,
  anchorEl,
  selectedEvent,
  onClose,
  onEdit,
  onRefreshCalendar,
  onPublicationMarksChanged,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslationApp();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatchApp();
  const [isRetryPublishing, setIsRetryPublishing] = useState(false);

  const confirmRetryAllPlatformsPublishing = useCallback(async () => {
    if (!selectedEvent) return false;

    const scheduleID =
      selectedEvent.originalData?.scheduleID ?? selectedEvent.id;
    if (!scheduleID) return false;

    const retryablePlatforms =
      getRetryablePlatformsFromScheduledEvent(selectedEvent);
    if (retryablePlatforms.length === 0) return false;

    const od = selectedEvent.originalData ?? {};
    const fullPlatformsData =
      od.platformsData && typeof od.platformsData === "object"
        ? od.platformsData
        : {};
    const channelStates = selectedEvent.states || od.states || {};

    const retryDetailRows = buildFailureRowsFromScheduledContent({
      platforms: retryablePlatforms,
      states: channelStates,
      platformsData: fullPlatformsData,
      t,
    });

    const platformsLabelCsv = retryablePlatforms
      .map((platform) => getOrganicPlatformLabel(platform, t))
      .join(", ");

    const confirmation = await confirmOrganicPublishRetry({
      rows: retryDetailRows,
      platformsLabel: platformsLabelCsv,
      t,
    });

    if (!confirmation?.isConfirmed) return false;

    const urlsSource = od.urls;
    const normalizedUrls = Array.isArray(urlsSource)
      ? urlsSource
          .map((u) => (typeof u === "string" ? u : u?.url))
          .filter(Boolean)
      : [];

    const platformsDataPayload = retryablePlatforms.reduce((acc, platform) => {
      if (fullPlatformsData[platform]) {
        acc[platform] = fullPlatformsData[platform];
      }
      return acc;
    }, {});

    const basePayload = {
      platforms: retryablePlatforms,
      isRetry: true,
      scheduleID,
      platformsData: platformsDataPayload,
    };

    if (normalizedUrls.length > 0) {
      basePayload.urls = normalizedUrls;
    }
    if (od.language) {
      basePayload.language = od.language;
    }
    if (typeof od.description === "string") {
      basePayload.description = od.description;
    }

    setIsRetryPublishing(true);
    try {
      const retryFlowResult = await runOrganicPublishRetryFlow({
        dispatch,
        initialPayload: basePayload,
        t,
        buildRetryPayload: ({ failedPlatforms }) => ({
          ...basePayload,
          platforms: failedPlatforms,
          platformsData: failedPlatforms.reduce((acc, platform) => {
            if (fullPlatformsData[platform]) {
              acc[platform] = fullPlatformsData[platform];
            }
            return acc;
          }, {}),
          isRetry: true,
        }),
      });

      if (retryFlowResult.isSuccess) {
        SimpleAlert({
          icon: "success",
          title: t(READY),
          text: t(ALERT_SHARE_SUCCESS),
        });

        if (onRefreshCalendar) {
          onRefreshCalendar();
        }
        onPublicationMarksChanged?.({
          contentIDs: collectScheduledContentIDs(
            selectedEvent.originalData || selectedEvent
          ),
        });
        return true;
      }

      SimpleAlert({
        icon: "error",
        title: t(ERROR),
        text: t(ORGANIC_PUBLISH_RETRY_ERROR),
      });
      return false;
    } catch {
      SimpleAlert({
        icon: "error",
        title: t(ERROR),
        text: t(ORGANIC_PUBLISH_RETRY_ERROR),
      });
      return false;
    } finally {
      setIsRetryPublishing(false);
    }
  }, [
    dispatch,
    onPublicationMarksChanged,
    onRefreshCalendar,
    selectedEvent,
    t,
  ]);

  if (!selectedEvent) return null;

  const locale = getDateFnsLocale(i18n.language);
  const isDemoContent = selectedEvent.originalData?.isDemoContent === true;
  const scheduleIDForRetry =
    selectedEvent.originalData?.scheduleID ?? selectedEvent.id;
  const showRetryPublishing =
    !isDemoContent &&
    typeof scheduleIDForRetry === "string" &&
    scheduleIDForRetry.length > 0 &&
    isScheduledEventRetryable(selectedEvent);

  const onEditEvent = (event) => {
    if (onEdit) {
      onEdit(event);
      onClose();
    }
  };

  // Secondary button styles (no hover effect)
  const chipSecondaryStyles = {
    backgroundColor: theme.palette.button.secondary.main,
    color: theme.palette.button.secondary.contrastText,
    border: `1px solid ${INPUT_COLOR_BORDER}`,
    boxShadow: BOX_SHADOW_BUTTON,
    fontSize: FONT_SIZE_BUTTON,
    borderRadius: BORDER_RADIUS_BUTTON,
    fontWeight: 600,
  };

  // Mobile version with Dialog
  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{
          sx: {
            height: "85vh",
            width: "90vw",
            borderRadius: BORDER_RADIUS,
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexShrink: 0,
            }}
          >
            <ActionButtons
              selectedEvent={selectedEvent}
              onClose={onClose}
              onEdit={onEditEvent}
              onRefreshCalendar={onRefreshCalendar}
              onPublicationMarksChanged={onPublicationMarksChanged}
              isMobile={true}
            />
            <IconButton
              onClick={onClose}
              sx={{
                padding: 0.5,
                color: "text.secondary",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "#f2f2f2",
                },
              }}
              size="small"
            >
              <CloseIcon fontSize="medium" />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
            <EventTitle title={selectedEvent.title} isMobile={true} />
            <Box>
              <EventDateAndTime
                startDate={selectedEvent.start}
                locale={locale}
                timezone={
                  selectedEvent.timezone || selectedEvent.originalData?.timezone
                }
              />
            </Box>

            <EventDescription
              description={selectedEvent.originalData?.description}
            />
            <Box sx={{ mb: 2 }}>
              <MobileNetworkAndStatus
                selectedEvent={selectedEvent}
                chipStyles={chipSecondaryStyles}
                showRetryPublishing={showRetryPublishing}
                onRetryPublishing={confirmRetryAllPlatformsPublishing}
                isRetryPublishing={isRetryPublishing}
              />
            </Box>
            <MobileEventContent selectedEvent={selectedEvent} />
          </Box>
        </Box>
      </Dialog>
    );
  }

  // Desktop version with Popover
  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{ zIndex: APP_DROPDOWN_Z_INDEX_CSS }}
        PaperProps={{
          sx: {
            width: 450,
            maxWidth: "90vw",
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            mt: 1,
          },
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        disableRestoreFocus
      >
        <Box sx={{ p: 3, position: "relative" }}>
          <CloseButton onClose={onClose} />
          <EventTitle title={selectedEvent.title} />
          <ActionButtons
            selectedEvent={selectedEvent}
            onClose={onClose}
            onEdit={onEditEvent}
            onRefreshCalendar={onRefreshCalendar}
            onPublicationMarksChanged={onPublicationMarksChanged}
            isMobile={false}
          />
          <EventDescription
            description={selectedEvent.originalData?.description}
          />
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 2,
              alignItems: "flex-start",
            }}
          >
            <EventInfoColumn
              selectedEvent={selectedEvent}
              locale={locale}
              chipStyles={chipSecondaryStyles}
              showRetryPublishing={showRetryPublishing}
              onRetryPublishing={confirmRetryAllPlatformsPublishing}
              isRetryPublishing={isRetryPublishing}
            />
            <EventContentColumn selectedEvent={selectedEvent} />
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default EventDetailsPopover;
